import buildQuery from 'odata-query';

import type { ODataQueryOptions } from '../notifications';
import {
    axiosDelete,
    get,
    post,
} from '../utility/Requests';
import type { Role } from './OrchRoleAssignmentService';

const ORCH_URL = '/api/orchestrator/folders';

export enum FolderType {
    Standard = 'Standard',
    Personal = 'Personal',
    Virtual = 'Virtual',
    Solution = 'Solution',
}

export enum FeedType {
    Undefined = 'Undefined',
    Processes = 'Processes',
    Libraries = 'Libraries',
    PersonalWorkspace = 'PersonalWorkspace',
    FolderHierarchy = 'FolderHierarchy',
}

export type NewFolder = {
    DisplayName: string;
    FolderType: FolderType;
    FeedType: FeedType;
};

export type Folder = NewFolder & {
    IsSelectable?: boolean;
    HasChildren?: boolean;
    Level?: number;
    Key: string;
    FullyQualifiedName: string;
    Description?: string;
    IsPersonal?: boolean;
    ProvisionType: 'Manual' | 'Automatic';
    PermissionModel: 'InheritFromTenant' | 'FineGrained';
    ParentId?: number;
    ParentKey?: string;
    Id: number;
};

type FolderDto = {
    '@odata.count': number;
    value: Folder[];
};

export type CurrentUserFolderDto = {
    Count: number;
    PageItems: Folder[];
};

export type UserEntity = {
    UserName: string;
    IsInherited: boolean;
    MayHaveAttended: boolean;
    MayHaveUnattended: boolean;
    Type: string;
    Source: string;
    Id: number;
    FullName: string;
    AuthenticationSource: string;
};

export type UserRole = {
    HasAlertsEnabled: boolean;
    Id: number;
    UserEntity: UserEntity;
    Roles: Role[];
};

export type UserRolesResponse = {
    '@odata.context': string;
    '@odata.count': number;
    value: UserRole[];
};

export interface MachineToFolderAssociation {
    associations: {
        FolderId: number;
        AddedMachineIds: number[];
        RemovedMachineIds: number[];
    };
}

export async function getAllFolders(
    urlParams: { accountLogicalName: string; tenantName: string },
    queryOptions: ODataQueryOptions
) {
    const {
        accountLogicalName, tenantName,
    } = urlParams;
    return await get<FolderDto>(`${ORCH_URL}/getAllFolders/${accountLogicalName}/${tenantName}${buildQuery(queryOptions)}`);
}

export async function getAllFoldersForCurrentUser(urlParams: { accountLogicalName: string; tenantName: string }) {
    const {
        accountLogicalName, tenantName,
    } = urlParams;
    return await get<CurrentUserFolderDto>(`${ORCH_URL}/getAllFoldersForCurrentUser/${accountLogicalName}/${tenantName}`);
}

export async function getAllFoldersForCurrentUserV2(tenantName: string) {
    const url = `/${tenantName}/orchestrator_/api/FoldersNavigation/GetAllFoldersForCurrentUser`;
    return get<Folder[]>(url, { stripPortalPath: true }).then((data) => data);
}

export async function getFolder(urlParams: { accountLogicalName: string; tenantName: string }, folderKey: string) {
    const {
        accountLogicalName, tenantName,
    } = urlParams;
    return await get<Folder>(`${ORCH_URL}/${accountLogicalName}/${tenantName}/${folderKey}`);
}

export async function createFolder(urlParams: { accountLogicalName: string; tenantName: string }, folder: NewFolder) {
    const {
        accountLogicalName, tenantName,
    } = urlParams;
    return await post<Folder>(`${ORCH_URL}/${accountLogicalName}/${tenantName}`, { body: folder });
}

export async function deleteFolder(urlParams: { accountLogicalName: string; tenantName: string }, folderKey: string) {
    const {
        accountLogicalName, tenantName,
    } = urlParams;
    return await axiosDelete(`${ORCH_URL}/${accountLogicalName}/${tenantName}/${folderKey}`);
}

export async function getOrchUsersForFolder(body: { folderId: number; tenantName: string; organizationName: string }) {
    return await get<UserRolesResponse>(`${ORCH_URL}/getUsersForFolder/${body.organizationName}/${body.tenantName}/${body.folderId}`);
}

export async function updateMachinesToFolderAssociations(organizationName: string, tenantName: string, folderId: string, body: MachineToFolderAssociation) {
    return await post(
        `${ORCH_URL}/UpdateMachinesToFolderAssociations/${organizationName}/${tenantName}`,
        {
            body,
            headers: {
                extendRequestHeaders: 'true',
                'x-uipath-organizationunitid': folderId,
            },
        }
    );
}
